import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, MenuItem, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import AddIcon from '@material-ui/icons/Add'
import { MATERIAL_CATEGORIES, COLOR_FAMILIES } from './offeringHelpers'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    marginBottom: 20
  },
  filter: {
    minWidth: 140
  },
  search: {
    minWidth: 180
  },
  resetBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--ink-4)',
    padding: '4px 8px',
    minWidth: 0,
    '&:hover': {
      color: 'var(--ink-2)',
      background: 'var(--bg-2)'
    }
  },
  segmented: {
    background: 'var(--bg-2)',
    border: '1px solid var(--line)',
    borderRadius: 8,
    padding: 2,
    display: 'inline-flex',
    gap: 2
  },
  segBtn: {
    border: 0,
    background: 'transparent',
    fontSize: 13,
    fontWeight: 500,
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
    color: 'var(--ink-3)',
    transition: 'all 0.15s ease',
    minWidth: 0,
    textTransform: 'none',
    '&:hover': {
      color: 'var(--ink)'
    }
  },
  segBtnActive: {
    border: 0,
    background: 'var(--surface)',
    fontSize: 13,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
    color: 'var(--ink)',
    boxShadow: 'var(--shadow-sm)',
    minWidth: 0,
    textTransform: 'none'
  },
  resultCount: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--ink-4)',
    marginLeft: 'auto',
    whiteSpace: 'nowrap'
  }
}))

export default function ComparisonFilterBar ({
  filters,
  onFilterChange,
  onReset,
  vendors,
  viewMode,
  onViewModeChange,
  total,
  onOpenQuickCalc,
  onOpenCreate
}) {
  const classes = useStyles()
  const [searchLocal, setSearchLocal] = useState(filters.search || '')
  const debounceRef = useRef(null)

  // Sync search debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (searchLocal !== filters.search) {
        onFilterChange('search', searchLocal)
      }
    }, 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchLocal]) // eslint-disable-line

  // Reset local search when filters reset
  useEffect(() => {
    if (!filters.search && searchLocal) setSearchLocal('')
  }, [filters.search]) // eslint-disable-line

  return (
    <div className={classes.root}>
      {viewMode === 'comparison' && (
        <>
          {/* Category */}
          <TextField
            select
            label="Category"
            value={filters.materialCategory || ''}
            onChange={(e) => onFilterChange('materialCategory', e.target.value)}
            size="small"
            variant="outlined"
            className={classes.filter}
          >
            <MenuItem value="">All</MenuItem>
            {MATERIAL_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>

          {/* Color */}
          <TextField
            select
            label="Color"
            value={filters.colorFamily || ''}
            onChange={(e) => onFilterChange('colorFamily', e.target.value)}
            size="small"
            variant="outlined"
            className={classes.filter}
          >
            <MenuItem value="">All</MenuItem>
            {COLOR_FAMILIES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>

          {/* Vendor */}
          <TextField
            select
            label="Vendor"
            value={filters.vendorId || ''}
            onChange={(e) => onFilterChange('vendorId', e.target.value)}
            size="small"
            variant="outlined"
            className={classes.filter}
          >
            <MenuItem value="">All</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
            ))}
          </TextField>

          {/* Search */}
          <TextField
            label="Search"
            value={searchLocal}
            onChange={(e) => setSearchLocal(e.target.value)}
            size="small"
            variant="outlined"
            className={classes.search}
            placeholder="Material name..."
          />

          {/* Reset */}
          <Button
            className={classes.resetBtn}
            onClick={() => { setSearchLocal(''); onReset() }}
          >
            RESET
          </Button>
        </>
      )}

      {/* Quick Calculator */}
      {onOpenQuickCalc && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LocalShippingIcon style={{ fontSize: 16 }} />}
          onClick={onOpenQuickCalc}
          style={{ textTransform: 'none', fontWeight: 600 }}
        >
          Quick Calculator
        </Button>
      )}

      {/* Add Offering */}
      {onOpenCreate && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon style={{ fontSize: 16 }} />}
          onClick={onOpenCreate}
          style={{ textTransform: 'none', fontWeight: 600 }}
        >
          Add
        </Button>
      )}

      {/* View toggle */}
      <Box className={classes.segmented}>
        <button
          className={viewMode === 'comparison' ? classes.segBtnActive : classes.segBtn}
          onClick={() => onViewModeChange('comparison')}
        >
          Grid
        </button>
        <button
          className={viewMode === 'table' ? classes.segBtnActive : classes.segBtn}
          onClick={() => onViewModeChange('table')}
        >
          Table
        </button>
      </Box>

      {/* Result count */}
      {total != null && (
        <span className={classes.resultCount}>
          {total} result{total !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  )
}
